import React, {
  ReactElement,
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';

import {ScrollTrigger} from 'gsap/ScrollTrigger';

import {logAnalyticsEvent} from '../utils/AnalyticsUtils';
import useNavigateToConnectIq from '../utils/useNavigateToConnectIq';
import {RegionalPrice, useRegionalPrice} from './PriceUtils';
import {WatchFaceContext, WatchFaceContextType} from './WatchFaceContext';
import './css/ConnectIqNavDialog.css';

export function ConnectIqNavDialog(): ReactElement {
  const ctx = useContext(ConnectIqNavDialogContext);

  const [watchDevice, setWatchDevice] = useState('');
  const deviceSupportedStatus = DEVICES.find(
    d => d.id === watchDevice,
  )?.supported;

  const navigateToConnectIq = useNavigateToConnectIq({
    elementName: ctx.dialogEntryPoint ?? 'none',
    regionalPrice: ctx.regionalPrice,
    watchFaceCtx: ctx.dialogAppInfo ?? {
      appId: '',
      appName: 'null',
      priceUsd: null,
    },
  });

  function closeConnectIqNavDialog(): void {
    logAnalyticsEvent('close_ciq_dialog');
    toggleConnectIqNavDialog(false);
  }

  const onDeviceSelected = (devId: string) => {
    logAnalyticsEvent('device_selected', {device_id: devId});
    setWatchDevice(devId);
  };

  let appParagraph: ReactNode;

  // if (isIOS || isAndroid) {
  //   const os = isIOS ? 'iOS' : 'Android';
  //   appParagraph = (
  //     <p>
  //       <small>
  //         If you have the Connect IQ app installed on your {os} device, it will
  //         open automatically. Otherwise, you&apos;ll be taken to{' '}
  //         <u>apps.garmin.com</u>.
  //       </small>
  //     </p>
  //   );
  // }

  let maybeBack = '';
  try {
    const searchParams = new URLSearchParams(window.location.search);
    const utm_source = searchParams.get('utm_source');
    if (utm_source === 'connect_iq') {
      maybeBack = ' back';
    }
  } catch (e) {
    console.error("Couldn't get utm_source");
  }

  return (
    <>
      <div className="connect-iq-nav-dialog-positioning">
        <div
          className="connect-iq-nav-dialog-overlay"
          onClick={closeConnectIqNavDialog}
        />
        <div className="connect-iq-nav-dialog">
          <h3>
            Redirecting{maybeBack} to <b>Connect IQ Store</b>
          </h3>
          {ctx.dialogAppInfo?.priceUsd != null ? (
            <p>
              To finish your purchase, you&apos;ll be redirected{maybeBack} to
              Garmin&apos;s Connect IQ store.
            </p>
          ) : (
            <p>
              To download the watch face, you&apos;ll be redirected{maybeBack}{' '}
              to Garmin&apos;s Connect IQ store.
            </p>
          )}
          <p>
            <small>
              Connect IQ is Garmin&apos;s official platform for downloading
              custom watch faces directly to your device.
            </small>
          </p>
          {appParagraph}
          <p>
            <b>Choose your device</b>
            <br />
            <select
              className="required"
              onChange={e => onDeviceSelected(e.target.value)}>
              <option>Not selected</option>
              {[...DEVICES]
                .sort(
                  (a, b) => a._tier - b._tier || a.title.localeCompare(b.title),
                )
                .map(d => (
                  <option key={d.id} value={d.id}>
                    {d.title}
                  </option>
                ))}
            </select>
          </p>
          {watchDevice ? (
            deviceSupportedStatus === 'garmin_no' ? (
              <p className="error_text">
                Sorry, this watch model is not supported.
              </p>
            ) : deviceSupportedStatus === 'other_no' ? (
              <p className="error_text">
                This watch face is exclusively for Garmin devices.
              </p>
            ) : null
          ) : null}
          <div className="connect-iq-nav-dialog-buttons">
            <button
              disabled={!watchDevice || deviceSupportedStatus !== 'yes'}
              onClick={() => navigateToConnectIq()}>
              Open Connect IQ
            </button>
            <button
              className="button-secondary"
              onClick={closeConnectIqNavDialog}>
              Back
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export function useOpenConnectIqNavDialog(
  appId: string,
  dialogEntryPoint: string,
): () => void {
  const ctx = useContext(ConnectIqNavDialogContext);
  const watchFaceCtx = useContext(WatchFaceContext);
  const regionalPrice = useRegionalPrice();

  return () => {
    ctx.updateDialogCtx(watchFaceCtx, regionalPrice, dialogEntryPoint);
    toggleConnectIqNavDialog(true);
    logAnalyticsEvent('open_ciq_dialog');
  };
}

function toggleConnectIqNavDialog(open: boolean): void {
  const el = document.getElementsByClassName(
    'connect-iq-nav-dialog-positioning',
  )[0];

  if (!el) {
    console.error('Cookie Dialog element not found');
  }

  document.body.classList.toggle('noscroll', open);
  el.classList.toggle(OPEN_CLASSNAME, open);
  ScrollTrigger.refresh();
}

const OPEN_CLASSNAME = 'open';

export type ConnectIqNavDialogContextType = {
  dialogAppInfo: WatchFaceContextType | undefined;
  dialogEntryPoint: string | undefined;
  regionalPrice: RegionalPrice | null;
  updateDialogCtx: (
    dialogAppInfo: WatchFaceContextType | undefined,
    regionalPrice: RegionalPrice | null,
    dialogEntryPoint: string | undefined,
  ) => void;
};

export const ConnectIqNavDialogContext =
  createContext<ConnectIqNavDialogContextType>({
    dialogAppInfo: undefined,
    dialogEntryPoint: undefined,
    regionalPrice: null,
    updateDialogCtx: () => {
      console.error('No ConnectIqNavDialogContext.Provider above');
      throw new Error('No ConnectIqNavDialogContext.Provider above');
    },
  });

type Props = {
  children: ReactNode;
};

export function ConnectIqNavDialogContextProvider({
  children,
}: Props): ReactElement {
  const [state, setState] = useState<
    Omit<ConnectIqNavDialogContextType, 'updateDialogCtx'>
  >({
    dialogAppInfo: undefined,
    dialogEntryPoint: undefined,
    regionalPrice: null,
  });

  const ctxValue = useMemo((): ConnectIqNavDialogContextType => {
    return {
      ...state,
      updateDialogCtx(
        dialogAppInfo: WatchFaceContextType | undefined,
        regionalPrice: RegionalPrice | null,
        dialogEntryPoint: string | undefined,
      ) {
        setState({dialogAppInfo, dialogEntryPoint, regionalPrice});
      },
    };
  }, [state]);

  return (
    <ConnectIqNavDialogContext.Provider value={ctxValue}>
      {children}
    </ConnectIqNavDialogContext.Provider>
  );
}

type Supported = 'yes' | 'garmin_no' | 'other_no';
type DeviceDesc = {
  _tier: number;
  id: string;
  supported: Supported;
  title: string;
};

const DEVICES: DeviceDesc[] = [
  // Tier 0 - Non-Garmin devices
  {_tier: 0, id: 'apple', supported: 'other_no', title: 'Apple Watch'},
  {_tier: 0, id: 'fitbit', supported: 'other_no', title: 'Fitbit watches'},
  {_tier: 0, id: 'huawei', supported: 'other_no', title: 'Huawei watches'},
  {_tier: 0, id: 'pixel', supported: 'other_no', title: 'Pixel Watch'},
  {
    _tier: 0,
    id: 'galaxy',
    supported: 'other_no',
    title: 'Samsung Galaxy Watch',
  },

  // Tier 1 - Primary supported devices
  {_tier: 1, id: 'enduro_3', supported: 'yes', title: 'Enduro 3'},
  {_tier: 1, id: 'epix_2', supported: 'yes', title: 'epix (Gen 2)'},
  {_tier: 1, id: 'epix_pro_2', supported: 'yes', title: 'epix Pro (Gen 2)'},
  {_tier: 1, id: 'fenix_7', supported: 'yes', title: 'fēnix 7'},
  {_tier: 1, id: 'fenix_8', supported: 'yes', title: 'fēnix 8'},
  {_tier: 1, id: 'fenix_e', supported: 'yes', title: 'fēnix E'},
  {_tier: 1, id: 'forerunner_165', supported: 'yes', title: 'Forerunner 165'},
  {
    _tier: 1,
    id: 'forerunner_255',
    supported: 'yes',
    title: 'Forerunner 255 / 255s',
  },
  {
    _tier: 1,
    id: 'forerunner_265',
    supported: 'yes',
    title: 'Forerunner 265 / 265s',
  },
  {_tier: 1, id: 'forerunner_570', supported: 'yes', title: 'Forerunner 570'},
  {_tier: 1, id: 'forerunner_955', supported: 'yes', title: 'Forerunner 955'},
  {_tier: 1, id: 'forerunner_965', supported: 'yes', title: 'Forerunner 965'},
  {_tier: 1, id: 'forerunner_970', supported: 'yes', title: 'Forerunner 970'},
  {
    _tier: 1,
    id: 'instinct_3_amoled',
    supported: 'yes',
    title: 'Instinct 3 AMOLED',
  },
  {_tier: 1, id: 'tactix_8', supported: 'yes', title: 'tactix 8'},
  {_tier: 1, id: 'venu_3', supported: 'yes', title: 'Venu 3 / 3S'},
  {_tier: 1, id: 'venu_4', supported: 'yes', title: 'Venu 4'},
  {_tier: 1, id: 'vivoactive_5', supported: 'yes', title: 'vívoactive 5'},
  {_tier: 1, id: 'vivoactive_6', supported: 'yes', title: 'vívoactive 6'},

  // Tier 2 - Older supported devices
  {_tier: 2, id: 'approach_s50', supported: 'yes', title: 'Approach S50'},
  {_tier: 2, id: 'approach_s70', supported: 'yes', title: 'Approach S70'},
  {_tier: 2, id: 'd2_air', supported: 'yes', title: 'D2 Air X10 / X15'},
  {_tier: 2, id: 'd2_mach1', supported: 'yes', title: 'D2 Mach 1 / Mach 1 Pro'},
  {_tier: 2, id: 'descent_g2', supported: 'yes', title: 'Descent G2'},
  {_tier: 2, id: 'descent_mk3', supported: 'yes', title: 'Descent Mk3 / Mk3i'},
  {_tier: 2, id: 'enduro_2', supported: 'yes', title: 'Enduro 2'},
  {_tier: 2, id: 'marq_2', supported: 'yes', title: 'MARQ (Gen 2)'},
  {_tier: 2, id: 'quatix_7', supported: 'yes', title: 'quatix 7 / 7 Pro'},
  {_tier: 2, id: 'tactix_7', supported: 'yes', title: 'tactix 7'},
  {_tier: 2, id: 'venu_2', supported: 'yes', title: 'Venu 2 / 2S / 2 Plus'},

  // Tier 3 - Unsupported Garmin devices
  {_tier: 3, id: 'approach_s60', supported: 'garmin_no', title: 'Approach S60'},
  {_tier: 3, id: 'approach_s62', supported: 'garmin_no', title: 'Approach S62'},
  {_tier: 3, id: 'd2', supported: 'garmin_no', title: 'D2'},
  {_tier: 3, id: 'descent_g1', supported: 'garmin_no', title: 'Descent G1'},
  {_tier: 3, id: 'descent_mk1', supported: 'garmin_no', title: 'Descent Mk1'},
  {
    _tier: 3,
    id: 'descent_mk2',
    supported: 'garmin_no',
    title: 'Descent Mk2 / Mk2i',
  },
  {_tier: 3, id: 'enduro', supported: 'garmin_no', title: 'Enduro'},
  {_tier: 3, id: 'epix', supported: 'garmin_no', title: 'epix'},
  {_tier: 3, id: 'fenix_3', supported: 'garmin_no', title: 'fēnix 3'},
  {_tier: 3, id: 'fenix_5', supported: 'garmin_no', title: 'fēnix 5 / 5 Plus'},
  {
    _tier: 3,
    id: 'fenix_5s',
    supported: 'garmin_no',
    title: 'fēnix 5S / 5S Plus',
  },
  {
    _tier: 3,
    id: 'fenix_5x',
    supported: 'garmin_no',
    title: 'fēnix 5X / 5X Plus',
  },
  {_tier: 3, id: 'fenix_6', supported: 'garmin_no', title: 'fēnix 6 / 6S / 6X'},
  {_tier: 3, id: 'fenix_6_pro', supported: 'garmin_no', title: 'fēnix 6 Pro'},
  {_tier: 3, id: 'fenix_6s_pro', supported: 'garmin_no', title: 'fēnix 6S Pro'},
  {_tier: 3, id: 'fenix_6x_pro', supported: 'garmin_no', title: 'fēnix 6X Pro'},
  {
    _tier: 3,
    id: 'forerunner_45',
    supported: 'garmin_no',
    title: 'Forerunner 45',
  },
  {
    _tier: 3,
    id: 'forerunner_55',
    supported: 'garmin_no',
    title: 'Forerunner 55',
  },
  {
    _tier: 3,
    id: 'forerunner_230',
    supported: 'garmin_no',
    title: 'Forerunner 230',
  },
  {
    _tier: 3,
    id: 'forerunner_235',
    supported: 'garmin_no',
    title: 'Forerunner 235',
  },
  {
    _tier: 3,
    id: 'forerunner_245',
    supported: 'garmin_no',
    title: 'Forerunner 245',
  },
  {
    _tier: 3,
    id: 'forerunner_630',
    supported: 'garmin_no',
    title: 'Forerunner 630',
  },
  {
    _tier: 3,
    id: 'forerunner_645',
    supported: 'garmin_no',
    title: 'Forerunner 645',
  },
  {
    _tier: 3,
    id: 'forerunner_735',
    supported: 'garmin_no',
    title: 'Forerunner 735xt',
  },
  {
    _tier: 3,
    id: 'forerunner_745',
    supported: 'garmin_no',
    title: 'Forerunner 745',
  },
  {
    _tier: 3,
    id: 'forerunner_920',
    supported: 'garmin_no',
    title: 'Forerunner 920XT',
  },
  {
    _tier: 3,
    id: 'forerunner_935',
    supported: 'garmin_no',
    title: 'Forerunner 935',
  },
  {
    _tier: 3,
    id: 'forerunner_945',
    supported: 'garmin_no',
    title: 'Forerunner 945',
  },
  {_tier: 3, id: 'garmin_swim', supported: 'garmin_no', title: 'Garmin Swim 2'},
  {
    _tier: 3,
    id: 'instinct_2',
    supported: 'garmin_no',
    title: 'Instinct 2 / 2S / 2X',
  },
  {
    _tier: 3,
    id: 'instinct_3_solar',
    supported: 'garmin_no',
    title: 'Instinct 3 Solar',
  },
  {
    _tier: 3,
    id: 'instinct_co',
    supported: 'garmin_no',
    title: 'Instinct Crossover',
  },
  {_tier: 3, id: 'marq', supported: 'garmin_no', title: 'MARQ'},
  {_tier: 3, id: 'quatix_3', supported: 'garmin_no', title: 'quatix 3'},
  {_tier: 3, id: 'quatix_5', supported: 'garmin_no', title: 'quatix 5'},
  {_tier: 3, id: 'quatix_6', supported: 'garmin_no', title: 'quatix 6'},
  {
    _tier: 3,
    id: 'tactix_cdb',
    supported: 'garmin_no',
    title: 'tactix Charlie/Delta/Bravo',
  },
  {_tier: 3, id: 'venu', supported: 'garmin_no', title: 'Venu'},
  {_tier: 3, id: 'venu_sq', supported: 'garmin_no', title: 'Venu Sq'},
  {_tier: 3, id: 'venu_sq_2', supported: 'garmin_no', title: 'Venu Sq 2'},
  {_tier: 3, id: 'vivoactive', supported: 'garmin_no', title: 'vívoactive'},
  {_tier: 3, id: 'vivoactive_3', supported: 'garmin_no', title: 'vívoactive 3'},
  {
    _tier: 3,
    id: 'vivoactive_4',
    supported: 'garmin_no',
    title: 'vívoactive 4 / 4S',
  },

  // Tier 4 - The fallback for any devices not listed above
  {_tier: 4, id: 'other', supported: 'garmin_no', title: 'Other'},
];
